import { useMemo, useState } from "react";
import type { KeyboardEvent } from "react";
import MicroLabel from "@/components/base/MicroLabel";
import TermPanel from "@/pages/glossary/components/TermPanel";
import { glossaryTerms } from "@/mocks/siteData";

/* Position numbers stay tied to the full list, so a term keeps its number
   even when the reader narrows the list down with a search. */
const POSITIONS = new Map(glossaryTerms.map((term, index) => [term.term, index]));

const CATEGORY_ORDER = ["Basics", "Costs", "Order types", "Execution", "Risk"];

export default function GlossaryExplorer() {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(glossaryTerms[0].term);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === "") return glossaryTerms;
    return glossaryTerms.filter(
      (term) =>
        term.term.toLowerCase().includes(q) ||
        term.definition.toLowerCase().includes(q) ||
        term.category.toLowerCase().includes(q),
    );
  }, [query]);

  const activeTerm = filtered.find((term) => term.term === selected) ?? filtered[0] ?? null;
  const activeIndex = activeTerm ? filtered.indexOf(activeTerm) : -1;
  const activePosition = activeTerm ? POSITIONS.get(activeTerm.term) ?? 0 : 0;

  /* Group the visible terms so the list reads like a dictionary spread
     rather than one long undifferentiated column. */
  const groups = useMemo(() => {
    const order = CATEGORY_ORDER.filter((cat) => filtered.some((term) => term.category === cat));
    return order.map((category) => ({
      category,
      terms: filtered.filter((term) => term.category === category),
    }));
  }, [filtered]);

  const step = (delta: number) => {
    if (!activeTerm) return;
    const next = filtered[activeIndex + delta];
    if (next) setSelected(next.term);
  };

  const handleListKeys = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      step(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      step(-1);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
      <div className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-24">
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-950" aria-hidden="true">
            <i className="ri-search-line text-lg"></i>
          </span>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search terms and definitions"
            aria-label="Search the glossary"
            className="w-full pl-12 pr-11 py-3.5 border-2 border-foreground-950 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:bg-primary-50 transition-colors"
          />
          {query !== "" && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear the search field"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center border-2 border-foreground-950 bg-background-50 text-foreground-950 hover:bg-foreground-950 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          )}
        </div>

        <div
          tabIndex={0}
          onKeyDown={handleListKeys}
          aria-label="Glossary term list, use the arrow keys to move"
          className="mt-4 border-2 border-foreground-950 bg-background-50 max-h-[420px] lg:max-h-[calc(100vh-340px)] lg:min-h-[320px] overflow-y-auto focus:outline-none"
        >
          {groups.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="font-heading font-bold uppercase tracking-tight text-sm text-foreground-950">
                No terms match
              </p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                Nothing contains “{query.trim()}”. Try a shorter keyword.
              </p>
              <button
                type="button"
                onClick={() => setQuery("")}
                className="mt-4 inline-flex items-center gap-2 px-3 py-2 border-2 border-foreground-950 bg-primary-500 font-label text-[10px] uppercase tracking-[0.16em] font-semibold text-foreground-950 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line" aria-hidden="true"></i>
                Show all {glossaryTerms.length}
              </button>
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.category}>
                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-2 bg-background-100 border-b-2 border-foreground-950">
                  <MicroLabel tone="muted">{group.category}</MicroLabel>
                  <span className="font-label text-[10px] font-semibold tracking-[0.16em] text-foreground-400">
                    {String(group.terms.length).padStart(2, "0")}
                  </span>
                </div>
                {group.terms.map((term) => {
                  const isActive = activeTerm?.term === term.term;
                  return (
                    <div key={term.term}>
                      <button
                        type="button"
                        onClick={() => setSelected(term.term)}
                        aria-pressed={isActive}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-background-200 transition-colors cursor-pointer ${
                          isActive
                            ? "bg-foreground-950"
                            : "bg-background-50 hover:bg-primary-50"
                        }`}
                      >
                        <span
                          className={`w-5 shrink-0 font-label text-[10px] font-semibold tracking-[0.12em] ${
                            isActive ? "text-background-400" : "text-foreground-400"
                          }`}
                        >
                          {String((POSITIONS.get(term.term) ?? 0) + 1).padStart(2, "0")}
                        </span>
                        <span
                          className={`flex-1 min-w-0 truncate font-heading font-bold uppercase tracking-tight text-sm ${
                            isActive ? "text-primary-500" : "text-foreground-950"
                          }`}
                        >
                          {term.term}
                        </span>
                        <i
                          className={`shrink-0 text-base lg:hidden ${
                            isActive ? "text-primary-500 ri-subtract-line" : "text-foreground-400 ri-add-line"
                          }`}
                          aria-hidden="true"
                        ></i>
                      </button>

                      {isActive && (
                        <div className="lg:hidden">
                          <TermPanel
                            term={term}
                            position={activePosition}
                            total={glossaryTerms.length}
                            onPrev={() => step(-1)}
                            onNext={() => step(1)}
                            canPrev={activeIndex > 0}
                            canNext={activeIndex >= 0 && activeIndex < filtered.length - 1}
                            variant="inline"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="mt-3 flex items-center justify-between gap-3">
          <MicroLabel tone="muted">
            {query.trim() === ""
              ? `${glossaryTerms.length} terms in the dictionary`
              : `${filtered.length} of ${glossaryTerms.length} terms match`}
          </MicroLabel>
          <span className="hidden lg:inline-flex items-center gap-1.5 font-label text-[10px] uppercase tracking-[0.16em] font-semibold text-foreground-400">
            <i className="ri-arrow-up-down-line" aria-hidden="true"></i>
            Arrow keys
          </span>
        </div>
      </div>

      <div className="hidden lg:block lg:col-span-7 xl:col-span-8">
        {activeTerm ? (
          <TermPanel
            term={activeTerm}
            position={activePosition}
            total={glossaryTerms.length}
            onPrev={() => step(-1)}
            onNext={() => step(1)}
            canPrev={activeIndex > 0}
            canNext={activeIndex >= 0 && activeIndex < filtered.length - 1}
          />
        ) : (
          <div className="border-2 border-foreground-950 bg-background-100 px-8 py-16 text-center">
            <p className="font-heading font-black uppercase tracking-tight text-2xl text-foreground-950">
              Nothing filed under that
            </p>
            <p className="mt-3 text-sm text-foreground-600">
              Clear the search to walk the full dictionary again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}